import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';

import { useAppSelector } from '../../app/hook';
import { useRefreshTokenMutation } from '../../app/services/auth-rtk.service';
import { login, selectUserAuthStatus } from '../../features/auth/auth.slice';
import LoadingComponent from './loading/loading.component';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAppSelector(selectUserAuthStatus);
  const [isLoading, setIsLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const [refreshTokenRequest, refreshTokenRequestStatus] = useRefreshTokenMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const refreshAttempts = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) {
      const refreshToken = localStorage.getItem(`LOUISEBOT_refresh_token`);

      if (refreshToken) {
        // Check if max retries exceeded
        if (refreshAttempts.current >= 5) {
          console.warn('Too many refresh attempts. Redirecting to login.');
          navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/logout`, {
            replace: true,
          });
          return;
        }

        refreshAttempts.current += 1;
        setIsLoading(true);

        refreshTokenRequest({
          refresh_token: refreshToken,
        })
          .unwrap()
          .then(res => {
            localStorage.setItem(`LOUISEBOT_refresh_token`, res.element.refresh_token);
            localStorage.setItem(`LOUISEBOT_token`, res.element.access_token);
            localStorage.setItem(`LOUISEBOT_user_access_token`, res.element.userAccessToken);

            dispatch(
              login({
                is_remember_me: true,
              })
            );

            window.location.reload();
          })
          .catch(error => {
            // Don't increment here again (already incremented before request)
            navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/logout`, {
              replace: true,
            });
          });
      } else {
        navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/logout`, {
          replace: true,
        });
      }
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  if (isLoading) return <LoadingComponent />;
  else return <React.Fragment>{children}</React.Fragment>;
};

export default PrivateRoute;
